import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Sprout, ExternalLink, Check, X, GitCompareArrows, ShieldCheck } from "lucide-react";
import { fetchProgram, trackJoinClick } from "@/lib/api";
import { ProgramCard } from "@/components/ProgramCard";
import { useCompare } from "@/context/CompareContext";

const Metric = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5">
    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold mb-1">{label}</p>
    <p className={`font-heading text-xl font-medium ${accent ? "text-emerald-700" : ""}`}>{value}</p>
  </div>
);

export default function ProgramDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { toggle, has } = useCompare();

  useEffect(() => {
    setData(null);
    setNotFound(false);
    window.scrollTo(0, 0);
    fetchProgram(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <main className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-500">Program not found. <Link to="/programs" className="text-emerald-700 font-medium">Back to directory</Link></main>;
  if (!data) return <main className="max-w-4xl mx-auto px-4 py-24 text-center text-slate-400">Loading…</main>;

  const p = data.program;
  const inCompare = has(p.slug);

  return (
    <main data-testid="program-detail-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/programs" data-testid="back-to-directory-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200 mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8 mb-12">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-3 py-1 rounded-full">{p.niche_name}</span>
            {p.editors_choice && <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full"><BadgeCheck className="w-3.5 h-3.5" /> Editor's Pick</span>}
            {p.beginner_friendly && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full"><Sprout className="w-3.5 h-3.5" /> Beginner Friendly</span>}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight font-medium">{p.name}</h1>
          <p className="text-slate-500 mt-2">by {p.company}</p>
          <p className="text-slate-600 leading-relaxed max-w-2xl mt-4">{p.description}</p>
        </div>
        <div className="flex flex-col gap-3 shrink-0">
          <a href={p.join_url} target="_blank" rel="noopener noreferrer" data-testid="join-program-button"
            onClick={() => trackJoinClick(p.slug)}
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200">
            Join Program <ExternalLink className="w-4 h-4" />
          </a>
          <button onClick={() => toggle(p)} data-testid="detail-compare-button"
            className={`flex items-center justify-center gap-2 px-8 py-3 rounded-full border font-medium text-sm transition-colors duration-200 ${
              inCompare ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-700 hover:border-slate-900"
            }`}>
            <GitCompareArrows className="w-4 h-4" /> {inCompare ? "In comparison" : "Add to compare"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Metric label="Commission" value={p.commission} accent />
        <Metric label="Cookie Duration" value={`${p.cookie_days} days`} />
        <Metric label="Avg EPC" value={`$${p.epc.toFixed(2)}`} />
        <Metric label="Avg Order Value" value={p.aov ? `$${p.aov.toLocaleString()}` : "N/A"} />
        <Metric label="Payout Threshold" value={`$${p.payout_threshold}`} />
        <Metric label="Payout Frequency" value={p.payout_frequency} />
        <Metric label="Conversion Rate" value={`${p.conversion_rate}%`} />
        <Metric label="Approval Difficulty" value={p.difficulty.charAt(0).toUpperCase() + p.difficulty.slice(1)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <h2 className="font-heading text-lg font-semibold mb-5 flex items-center gap-2 text-emerald-700"><Check className="w-5 h-5" /> Pros</h2>
          <ul className="space-y-3">
            {p.pros.map((pro) => (
              <li key={pro} className="flex gap-3 text-sm text-slate-600 leading-relaxed"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{pro}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <h2 className="font-heading text-lg font-semibold mb-5 flex items-center gap-2 text-rose-600"><X className="w-5 h-5" /> Cons</h2>
          <ul className="space-y-3">
            {p.cons.map((con) => (
              <li key={con} className="flex gap-3 text-sm text-slate-600 leading-relaxed"><X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" />{con}</li>
            ))}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-8">
          <h2 className="font-heading text-lg font-semibold mb-5 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /> Program Details</h2>
          <dl className="space-y-4 text-sm">
            <div><dt className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Payment methods</dt><dd className="mt-1 flex flex-wrap gap-1.5">{p.payment_methods.map((m) => <span key={m} className="bg-slate-100 px-2.5 py-0.5 rounded-full text-xs font-medium">{m}</span>)}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Tracking</dt><dd className="mt-1 text-slate-600">{p.tracking_method}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Affiliate support</dt><dd className="mt-1 text-slate-600">{p.support_level}</dd></div>
            <div><dt className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Recurring commissions</dt><dd className="mt-1 text-slate-600">{p.recurring ? "Yes" : "No"}</dd></div>
          </dl>
        </div>
      </div>

      {data.related.length > 0 && (
        <section>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight mb-8">More in {p.niche_name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.related.map((r) => <ProgramCard key={r.slug} program={r} />)}
          </div>
        </section>
      )}
    </main>
  );
}
